import { useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { Link, useLocation } from 'react-router';
import { ApiFailure, api } from '../api.ts';
import { Notice } from '../components/Notice.tsx';
import { copy } from '../copy/en-CA.ts';

const c = copy.forgotPassword;

/**
 * "Forgot your password?" (owner, 4 November 2026): asks for the email a
 * person signs in with and has a reset link sent to it. The answer reads the
 * same whether or not an account uses the address, so the page cannot be
 * used to find out who has one; it says where to turn if nothing arrives.
 */
export function ForgotPasswordPage() {
  const location = useLocation();
  // The sign-in page passes on whatever was typed there, to save typing it twice.
  const typed = (location.state as { email?: unknown } | null)?.email;
  const [email, setEmail] = useState(typeof typed === 'string' ? typed : '');
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const sentHeading = useRef<HTMLHeadingElement>(null);

  // The form has gone; move focus to what replaced it, so a screen reader reads it.
  useEffect(() => {
    if (sentTo !== null) sentHeading.current?.focus();
  }, [sentTo]);

  const submit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setWorking(true);
    try {
      await api.requestPasswordReset(email);
      setSentTo(email.trim());
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="signin-page">
      <div className="wordmark">
        <span className="wordmark-name">{copy.appName}</span>
        <span className="wordmark-sub">{copy.appSub}</span>
      </div>
      {sentTo !== null ? (
        <div className="card signin-card">
          <div className="page-title">
            <h1 ref={sentHeading} tabIndex={-1}>
              {c.sentTitle}
            </h1>
            <p>{c.sent(sentTo)}</p>
          </div>
          <p className="muted">{c.nothingArrived}</p>
          <div className="row">
            <Link className="btn primary" to="/">
              {c.back}
            </Link>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setSentTo(null);
              }}
            >
              {c.otherEmail}
            </button>
          </div>
        </div>
      ) : (
        <form
          className="card signin-card"
          onSubmit={(e) => {
            void submit(e);
          }}
        >
          <div className="page-title">
            <h1>{c.title}</h1>
            <p>{c.intro}</p>
          </div>
          <div className="field">
            <label htmlFor="email">{c.email}</label>
            <input
              id="email"
              className="input"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
            />
          </div>
          {error ? <Notice kind="error">{error}</Notice> : null}
          <div className="row">
            <button type="submit" className="btn primary" disabled={working}>
              {working ? c.working : c.submit}
            </button>
            <Link className="btn" to="/">
              {c.back}
            </Link>
          </div>
        </form>
      )}
    </div>
  );
}
